import Phaser from 'phaser';
import { DEPTH, IS_PORTRAIT_LAYOUT, UI_CENTER_X } from '../core/Config';
import { GameSettings } from '../core/GameSettings';
import { DebugLog } from '../core/DebugLog';
import { createGameKeys, anyJustDown } from '../core/Keybinds';
import { SaveStore } from '../core/SaveStore';
import { ensureImage } from '../core/SceneAssetLoader';
import { NPCCullingManager } from '../core/NPCCulling';
import { PerformanceConfig } from '../core/PerformanceSettings';
import { markSceneInitComplete, markSceneInitStart } from '../core/RuntimeMonitor';
import { transitionTo } from '../core/SceneTransition';
import { NPCSprite } from '../engine/NPCSprite';
import { NPCBehaviorController } from '../engine/NPCBehaviorController';
import { Player } from '../engine/Player';
import { DataRegistry } from '../systems/DataRegistry';
import { DialogueSystem } from '../systems/DialogueSystem';
import { QuestSystem } from '../systems/QuestSystem';
import { DeveloperOverlay } from '../ui/DeveloperOverlay';
import { InteractionPrompt } from '../ui/InteractionPrompt';
import { UIScene } from './UIScene';

type SceneInitData = { houseId: string };

export class HouseScene extends Phaser.Scene {
  private dataRegistry!: DataRegistry;
  private player!: Player;
  private keys!: ReturnType<typeof createGameKeys>;
  private dialogueSystem!: DialogueSystem;
  private developerOverlay!: DeveloperOverlay;
  private currentNPCId?: string;
  private currentHouseId!: string;
  private npcZones = new Map<string, Phaser.GameObjects.Zone>();
  private npcControllers: NPCBehaviorController[] = [];
  private uiScene?: UIScene;
  private performance!: PerformanceConfig;
  private npcCullManager!: NPCCullingManager;
  private interactionHint!: Phaser.GameObjects.Text;
  private interactionPrompt!: InteractionPrompt;
  private isSceneReady = false;

  // Debug data
  private debugVisible = false;
  private debugGraphics!: Phaser.GameObjects.Graphics;
  private debugLabels: Phaser.GameObjects.Text[] = [];
  private exitZone?: Phaser.GameObjects.Zone;
  private houseEntrySpawn?: { x: number; y: number };

  constructor() {
    super('HouseScene');
  }

  private getUiScene(): UIScene | undefined {
    if (this.uiScene) return this.uiScene;
    if (!this.scene.isActive('UIScene')) return undefined;
    this.uiScene = this.scene.get('UIScene') as UIScene;
    return this.uiScene;
  }

  async create(data: SceneInitData) {
    this.isSceneReady = false;
    this.resetRuntimeState();
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.handleShutdown());
    DebugLog.info('HouseScene', 'create start', data);
    markSceneInitStart('HouseScene', data);

    try {
    this.currentHouseId = data.houseId;
    this.dataRegistry = this.registry.get('dataRegistry') as DataRegistry;

    const house = this.dataRegistry.getHouse(data.houseId);
    this.performance = this.registry.get('performanceConfig') as PerformanceConfig;

    await ensureImage(this, house.bgKey, house.bgPath);

    this.uiScene = this.getUiScene();
    const ui = this.getUiScene();

    ui?.events.emit('set-location', `${house.name} 내부`);
    ui?.events.emit('set-source', this.dataRegistry.sourceInfo);
    ui?.events.emit('refresh-settings-ui');
    ui?.setActionContext();
    ui?.teleportMenu?.setVillages(this.dataRegistry.getAllVillages(), house.villageId);

    this.physics.world.setBounds(0, 0, house.size.width, house.size.height);
    this.cameras.main.setBounds(0, 0, house.size.width, house.size.height);
    this.cameras.main.resetFX();
    this.cameras.main.fadeIn(120, 8, 12, 24);

    this.add
      .image(0, 0, house.bgKey)
      .setOrigin(0)
      .setDisplaySize(house.size.width, house.size.height)
      .setDepth(DEPTH.GROUND);

    this.keys = createGameKeys(this);

    this.player = new Player(this, house.entrySpawn.x, house.entrySpawn.y, this.keys);
    this.player.setCollideWorldBounds(true);

    this.interactionHint = this.add
      .text(UI_CENTER_X, 64, '', {
        fontSize: IS_PORTRAIT_LAYOUT ? '32px' : '22px',
        color: '#fff0b5',
        backgroundColor: '#0c120fdd',
        padding: { x: IS_PORTRAIT_LAYOUT ? 18 : 12, y: IS_PORTRAIT_LAYOUT ? 12 : 8 },
        wordWrap: { width: IS_PORTRAIT_LAYOUT ? 760 : 1200 },
      })
      .setPosition(UI_CENTER_X, IS_PORTRAIT_LAYOUT ? 126 : 64)
      .setOrigin(0.5, 0)
      .setScrollFactor(0)
      .setDepth(DEPTH.UI + 2)
      .setVisible(false);

    this.interactionPrompt = new InteractionPrompt(this);

    this.exitZone = this.add
      .zone(house.exitZone.x, house.exitZone.y, house.exitZone.w, house.exitZone.h)
      .setOrigin(0);

    this.physics.add.existing(this.exitZone, true);

    this.houseEntrySpawn = {
      x: house.entrySpawn.x,
      y: house.entrySpawn.y
    };

    this.physics.add.overlap(this.player, this.exitZone, () => {
      // overlap only; transition handled manually in update if needed
    });

    house.npcIds.forEach((npcId) => {
      const npc = this.dataRegistry.getNPC(npcId);

      const sprite = new NPCSprite(this, npc.x, npc.y, npc.spriteKey);
      const zone = this.add
        .zone(
          npc.interactionZone.x,
          npc.interactionZone.y,
          npc.interactionZone.w,
          npc.interactionZone.h
        )
        .setOrigin(0);

      this.physics.add.existing(zone, true);
      this.npcZones.set(npcId, zone);
      this.npcControllers.push(new NPCBehaviorController(this, sprite, npc.move));

      this.physics.add.overlap(this.player, zone, () => {
        this.currentNPCId = npcId;
      });
    });

    this.cameras.main.startFollow(this.player, true, 0.08, 0.08);
    this.cameras.main.centerOn(house.entrySpawn.x, house.entrySpawn.y);
    this.cameras.main.setZoom(Number(GameSettings.get('cameraZoom')));

    const latestUi = this.getUiScene();
    if (!latestUi) throw new Error('UIScene must be active before HouseScene creates dialogue UI.');
    this.dialogueSystem = new DialogueSystem(
      this,
      this.keys,
      latestUi.dialogueUI,
      latestUi.choiceUI,
      latestUi.actionPanel
    );

    this.developerOverlay = new DeveloperOverlay(this, {
      worldKey: this.currentHouseId,
      worldType: 'house'
    });

    this.npcCullManager = new NPCCullingManager(this, this.performance.npc);
    this.debugGraphics = this.add.graphics().setDepth(DEPTH.UI + 50).setScrollFactor(1);

    SaveStore.saveSession({
      sceneType: 'house',
      houseId: this.currentHouseId
    });

    this.tweens.add({
      targets: this.interactionHint,
      alpha: { from: 0.72, to: 1 },
      duration: 540,
      yoyo: true,
      repeat: -1
    });

    this.isSceneReady = true;
    markSceneInitComplete('HouseScene');
    QuestSystem.record('scene_enter', this.currentHouseId);
    DebugLog.info('HouseScene', 'create complete', { houseId: this.currentHouseId });
    } catch (error) {
      DebugLog.error('HouseScene', 'create failed', error);
      throw error;
    }
  }

  update(time: number) {
    if (!this.isSceneReady) return;
    if (!this.keys || !this.player || !this.dialogueSystem || !this.developerOverlay || !this.npcCullManager) return;

    const ui = this.getUiScene();
    const settingsPanel = ui?.settingsPanel;
    const portraitCatalogPanel = ui?.portraitCatalogPanel;
    const questLogPanel = ui?.questLogPanel;
    const teleportMenu = ui?.teleportMenu;
    const worldEditorPanel = ui?.worldEditorPanel;

    const canOpenMenu = !this.dialogueSystem.isActive();

    if (canOpenMenu && Phaser.Input.Keyboard.JustDown(this.keys.settingsMenu)) {
      ui?.toggleSettingsPanel();
    }

    if (canOpenMenu && Phaser.Input.Keyboard.JustDown(this.keys.portraitCatalog)) {
      ui?.togglePortraitCatalog();
    }

    if (canOpenMenu && Phaser.Input.Keyboard.JustDown(this.keys.teleportMenu)) {
      const house = this.dataRegistry.getHouse(this.currentHouseId);
      ui?.toggleTeleportMenu(house.villageId);
    }

    if (canOpenMenu && Phaser.Input.Keyboard.JustDown(this.keys.questLog)) {
      ui?.toggleQuestLogPanel();
    }

    if (canOpenMenu && Phaser.Input.Keyboard.JustDown(this.keys.editorPanel)) {
      ui?.toggleWorldEditorPanel();
    }

    if (anyJustDown([this.keys.cancel], ['cancel'])) {
      ui?.closePanels();
    }

    if (this.developerOverlay.isEnabled()) {
      if (Phaser.Input.Keyboard.JustDown(this.keys.editorGrid)) {
        this.developerOverlay.toggleGrid();
      }
      if (Phaser.Input.Keyboard.JustDown(this.keys.editorSnapDown)) {
        this.developerOverlay.changeSnap(-1);
      }
      if (Phaser.Input.Keyboard.JustDown(this.keys.editorSnapUp)) {
        this.developerOverlay.changeSnap(1);
      }
      worldEditorPanel?.render(this.developerOverlay.getPanelState());
    } else if (worldEditorPanel?.isVisible()) {
      worldEditorPanel.render(this.developerOverlay.getPanelState());
    }

    if (portraitCatalogPanel?.isVisible()) {
      if (anyJustDown(this.keys.choiceUp, ['up'])) portraitCatalogPanel.move(-1);
      if (anyJustDown(this.keys.choiceDown, ['down'])) portraitCatalogPanel.move(1);
      return;
    }

    if (questLogPanel?.isVisible()) {
      if (anyJustDown(this.keys.choiceUp, ['up'])) questLogPanel.move(-1);
      if (anyJustDown(this.keys.choiceDown, ['down'])) questLogPanel.move(1);
      return;
    }

    if (settingsPanel?.isVisible()) {
      if (anyJustDown(this.keys.choiceUp, ['up'])) settingsPanel.move(-1);
      if (anyJustDown(this.keys.choiceDown, ['down'])) settingsPanel.move(1);
      if (anyJustDown(this.keys.confirm, ['confirm', 'interact'])) {
        settingsPanel.confirm();
        this.cameras.main.setZoom(Number(GameSettings.get('cameraZoom')));
        ui?.events.emit('refresh-settings-ui');
      }
      return;
    }

    if (teleportMenu?.isVisible()) {
      if (anyJustDown(this.keys.choiceUp, ['up'])) teleportMenu.move(-1);
      if (anyJustDown(this.keys.choiceDown, ['down'])) teleportMenu.move(1);
      if (anyJustDown(this.keys.confirm, ['confirm', 'interact'])) {
        const selectedVillageId = teleportMenu.getSelectedVillageId();
        if (selectedVillageId) {
          DebugLog.info('HouseScene', 'teleport menu confirm', {
            fromHouseId: this.currentHouseId,
            toVillageId: selectedVillageId,
          });
          teleportMenu.hide();
          SaveStore.saveSession({
            sceneType: 'village',
            villageId: selectedVillageId
          });
          transitionTo(this, 'VillageScene', { villageId: selectedVillageId });
        }
      }
      return;
    }

    this.player.update(this.dialogueSystem.isActive());
    this.dialogueSystem.update();
    this.developerOverlay.update(this.player);
    this.npcCullManager.update(time, this.npcControllers);

    if (Phaser.Input.Keyboard.JustDown(this.keys.debug)) {
      this.debugVisible = !this.debugVisible;
      this.drawDebug();
    }

    let foundNpcId: string | undefined;

    for (const [npcId, zone] of this.npcZones) {
      if (Phaser.Geom.Intersects.RectangleToRectangle(this.player.getBounds(), zone.getBounds())) {
        foundNpcId = npcId;
        break;
      }
    }

    this.currentNPCId = foundNpcId;

    if (!this.dialogueSystem.isActive() && this.currentNPCId && anyJustDown(this.keys.interact, ['interact', 'confirm'])) {
      const npc = this.dataRegistry.getNPC(this.currentNPCId);
      const dialogue = this.dataRegistry.getDialogue(npc.dialogueId);
      this.dialogueSystem.start(dialogue, () => {
        this.currentNPCId = undefined;
      });
    }

    const house = this.dataRegistry.getHouse(this.currentHouseId);
    const isAtExit =
      !!this.exitZone &&
      Phaser.Geom.Intersects.RectangleToRectangle(
        this.player.getBounds(),
        this.exitZone.getBounds()
      );

    if (!this.dialogueSystem.isActive() && isAtExit && anyJustDown(this.keys.interact, ['interact', 'confirm'])) {
      const currentUi = this.getUiScene();
      currentUi?.teleportMenu?.hide();
      currentUi?.settingsPanel?.hide();

      SaveStore.saveSession({
        sceneType: 'village',
        villageId: house.villageId,
        spawnX: house.exitToVillageSpawn.x,
        spawnY: house.exitToVillageSpawn.y
      });

      transitionTo(this, 'VillageScene', {
        villageId: house.villageId,
        spawnX: house.exitToVillageSpawn.x,
        spawnY: house.exitToVillageSpawn.y
      });
      DebugLog.info('HouseScene', 'exit to village', {
        fromHouseId: this.currentHouseId,
        toVillageId: house.villageId,
        spawnX: house.exitToVillageSpawn.x,
        spawnY: house.exitToVillageSpawn.y,
      });
      return;
    }

    if (!this.dialogueSystem.isActive() && this.currentNPCId) {
      const npc = this.dataRegistry.getNPC(this.currentNPCId);
      this.interactionHint
        .setVisible(true)
        .setText(`${IS_PORTRAIT_LAYOUT ? 'A' : 'F / SPACE / ENTER'}  ·  ${npc.name}와 대화`);
      ui?.setActionContext('대화');
      this.interactionPrompt.showAt(
        npc.interactionZone.x + npc.interactionZone.w / 2,
        npc.interactionZone.y - 14,
        '대화'
      );
    } else if (!this.dialogueSystem.isActive() && isAtExit && this.exitZone) {
      this.interactionHint
        .setVisible(true)
        .setText(`${IS_PORTRAIT_LAYOUT ? 'A' : 'F / SPACE / ENTER'}  ·  밖으로 나가기`);
      ui?.setActionContext('나가기');
      this.interactionPrompt.showAt(
        this.exitZone.x + this.exitZone.width / 2,
        this.exitZone.y - 14,
        '나가기'
      );
    } else {
      this.interactionHint.setVisible(false);
      ui?.setActionContext();
      this.interactionPrompt.hide();
    }

    if (Phaser.Input.Keyboard.JustDown(this.keys.devOverlay) && this.keys.alt.isDown) {
      this.developerOverlay.toggle();
      worldEditorPanel?.render(this.developerOverlay.getPanelState());
    }

    SaveStore.saveSession({
      sceneType: 'house',
      houseId: this.currentHouseId
    });
  }

  private clearDebugLabels() {
    this.debugLabels.forEach((label) => label.destroy());
    this.debugLabels = [];
  }

  private resetRuntimeState() {
    this.npcControllers = [];
    this.npcZones.clear();
    this.currentNPCId = undefined;
    this.exitZone = undefined;
  }

  private handleShutdown() {
    this.isSceneReady = false;
    this.npcControllers = [];
    this.npcZones.clear();
    this.currentNPCId = undefined;
    SaveStore.flushSession();
  }

  private addDebugLabel(x: number, y: number, text: string, color = '#ffffff') {
    const label = this.add.text(x, y, text, {
      fontSize: '12px',
      color,
      backgroundColor: '#000000aa',
      padding: { x: 4, y: 2 }
    }).setDepth(DEPTH.UI + 60);

    this.debugLabels.push(label);
  }

  private drawDebug() {
    this.debugGraphics.clear();
    this.clearDebugLabels();

    if (!this.debugVisible) return;

    // 1) 출구 존 - 파랑
    if (this.exitZone) {
      this.debugGraphics.lineStyle(2, 0x3b82f6, 0.95);
      this.debugGraphics.strokeRect(
        this.exitZone.x,
        this.exitZone.y,
        this.exitZone.width,
        this.exitZone.height
      );
      this.addDebugLabel(this.exitZone.x, this.exitZone.y - 16, 'exit_zone', '#60a5fa');
    }

    // 2) NPC 상호작용 존 - 보라
    this.npcZones.forEach((zone, npcId) => {
      this.debugGraphics.lineStyle(2, 0xa855f7, 0.95);
      this.debugGraphics.strokeRect(zone.x, zone.y, zone.width, zone.height);
      this.addDebugLabel(zone.x, zone.y - 16, `npc:${npcId}`, '#d8b4fe');
    });

    // 3) 내부 시작 위치 - 초록
    if (this.houseEntrySpawn) {
      const x = this.houseEntrySpawn.x;
      const y = this.houseEntrySpawn.y;

      this.debugGraphics.lineStyle(3, 0x22c55e, 1);
      this.debugGraphics.strokeCircle(x, y, 12);
      this.debugGraphics.lineBetween(x - 14, y, x + 14, y);
      this.debugGraphics.lineBetween(x, y - 14, x, y + 14);

      this.addDebugLabel(x + 16, y - 10, 'entry_spawn', '#4ade80');
    }
  }
}
